import React, { useState } from "react";
import { Link } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import Modal from "../../components/common/Modal";
import {
  useGetUsersQuery,
  useDeleteUserMutation,
  useResetUserPasswordMutation,
} from "../../store/api";

const UserList = () => {
  const [filters, setFilters] = useState({ businessId: "", status: "", q: "" });
  const { data, isLoading, isError, error } = useGetUsersQuery(filters);
  const [deleteUser] = useDeleteUserMutation();
  const [resetPassword, resetState] = useResetUserPasswordMutation();

  // Modal state
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null, // 'reset' or 'delete'
    userId: null,
    userEmail: null,
  });

  const users = Array.isArray(data) ? data : [];

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const openModal = (type, userId, userEmail) => {
    setModalState({ isOpen: true, type, userId, userEmail });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, type: null, userId: null, userEmail: null });
  };

  const handleConfirmAction = async () => {
    const { type, userId } = modalState;

    try {
      if (type === "reset") {
        await resetPassword({ userId, payload: {} }).unwrap();
      } else if (type === "delete") {
        await deleteUser(userId).unwrap();
      }
      closeModal();
    } catch (err) {
      // error handled by UI
    }
  };

  const handleDelete = (userId, userEmail) => {
    openModal("delete", userId, userEmail);
  };

  const handleResetPassword = (userId, userEmail) => {
    openModal("reset", userId, userEmail);
  };

  const columns = [
    { key: "email", label: "Email" },
    { key: "displayName", label: "Name" },
    { key: "phone", label: "Phone" },
    { key: "status", label: "Status" },
    { key: "platformAdmin", label: "Platform Admin" },
    { key: "actions", label: "Actions" },
  ];

  const rows = users.map((user) => {
    const id = user.userId || user.id;
    return {
      id,
      email: user.email,
      displayName: user.displayName || "-",
      phone: user.phone || "-",
      status: user.status || "-",
      platformAdmin: user.isPlatformAdmin ? "Yes" : "-",
      actions: (
        <div className="flex gap-3">
          <Link
            to={`/users/${id}/roles`}
            className="text-sm font-semibold text-[#4f46e5]"
          >
            Roles
          </Link>
          <Link
            to={`/users/${id}/edit`}
            className="text-sm font-semibold text-[#4f46e5]"
          >
            Edit
          </Link>
          <button
            type="button"
            className="text-sm font-semibold text-[#4f46e5]"
            onClick={() => handleResetPassword(id, user.email)}
          >
            Reset Password
          </button>
          <button
            type="button"
            className="text-sm font-semibold text-rose-600"
            onClick={() => handleDelete(id, user.email)}
          >
            Disable
          </button>
        </div>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        subtitle="Manage system users and access."
        actions={
          <Link to="/users/new">
            <Button size="sm">Create User</Button>
          </Link>
        }
      />

      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px]">
          <Input
            name="q"
            placeholder="Search by email or name..."
            value={filters.q}
            onChange={handleFilterChange}
          />
        </div>
        <div className="w-[180px]">
          <Select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
          >
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Suspended">Suspended</option>
          </Select>
        </div>
        <div className="w-[180px]">
          <Input
            name="businessId"
            placeholder="Business ID..."
            value={filters.businessId}
            onChange={handleFilterChange}
          />
        </div>
      </div>

      <Card className="p-0">
        <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700">
          All Users
        </div>
        <div className="px-6 py-4">
          {isLoading && (
            <p className="text-sm text-slate-500">Loading users...</p>
          )}
          {isError && (
            <Alert tone="error">
              {error?.data?.detail || "Failed to load users."}
            </Alert>
          )}
          {!isLoading && !isError && (
            <Table columns={columns} rows={rows} emptyText="No users found." />
          )}
        </div>
      </Card>

      {/* Confirmation Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={
          modalState.type === "reset"
            ? "Reset Password"
            : modalState.type === "delete"
              ? "Disable User"
              : ""
        }
        message={
          modalState.type === "reset"
            ? `Are you sure you want to send a password reset email to ${modalState.userEmail}?`
            : modalState.type === "delete"
              ? `Are you sure you want to disable ${modalState.userEmail}? This action can be reversed later.`
              : ""
        }
        onConfirm={handleConfirmAction}
        confirmText={
          modalState.type === "reset" ? "Send Reset Email" : "Disable User"
        }
        confirmVariant={modalState.type === "delete" ? "primary" : "primary"}
        isLoading={resetState.isLoading}
      />
    </div>
  );
};

export default UserList;
